const BASE = '/spring/school';

const _LOGIN = (window.location.port === '3000' ? 'http://localhost:8080' : '') + '/spring/login';
function redirectToLogin() {
  window.location.href = _LOGIN + '?returnTo=' + encodeURIComponent(window.location.origin);
}


async function request(path, options = {}) {
  const res = await fetch(BASE + path, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
    ...options,
  });
  if (res.redirected && res.url.includes('login')) { redirectToLogin(); return null; }
  if (res.status === 401 || res.status === 403) { redirectToLogin(); return null; }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('text/html')) { redirectToLogin(); return null; }
    throw new Error(`${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return null;
  const data = await res.json();
  if (data && data.error === 'Not authenticated') { redirectToLogin(); return null; }
  return data;
}

export const api = {
  // Dashboard
  getStats: () => request('/dashboard/stats'),

  // Classes
  getClasses: () => request('/classes'),
  createClass: (data) => request('/classes', { method: 'POST', body: JSON.stringify(data) }),
  deleteClass: (id) => request(`/classes/${id}`, { method: 'DELETE' }),

  // Subjects
  getSubjects: () => request('/subjects'),
  createSubject: (data) => request('/subjects', { method: 'POST', body: JSON.stringify(data) }),

  // Teachers
  getTeachers: () => request('/teachers'),
  getTeacher: (id) => request(`/teachers/${id}`),
  createTeacher: (data) => request('/teachers', { method: 'POST', body: JSON.stringify(data) }),
  updateTeacher: (id, data) => request(`/teachers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTeacher: (id) => request(`/teachers/${id}`, { method: 'DELETE' }),

  // Students
  getStudents: () => request('/students'),
  getStudent: (id) => request(`/students/${id}`),
  getStudentsByClass: (classId) => request(`/students/class/${classId}`),
  createStudent: (data) => request('/students', { method: 'POST', body: JSON.stringify(data) }),
  updateStudent: (id, data) => request(`/students/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteStudent: (id) => request(`/students/${id}`, { method: 'DELETE' }),

  uploadStudentPhoto: (id, photo) =>
    request(`/students/${id}/photo`, { method: 'PUT', body: JSON.stringify({ photo }) }),

  // ID Cards
  getIdCardToken: (studentId) => request(`/id-card/token/${studentId}`),
  verifyIdCardToken: (token) => request('/id-card/verify', { method: 'POST', body: JSON.stringify({ token }) }),
  scanIdCardAttendance: (token) => request('/id-card/scan', { method: 'POST', body: JSON.stringify({ token }) }),

  // Assignments
  getAssignments: () => request('/assignments'),
  getAssignmentsByClass: (classId) => request(`/assignments/class/${classId}`),
  createAssignment: (data) => request('/assignments', { method: 'POST', body: JSON.stringify(data) }),
  deleteAssignment: (id) => request(`/assignments/${id}`, { method: 'DELETE' }),

  // Attendance
  getAttendanceByClass: (classId, date) => request(`/attendance/class/${classId}/date?date=${date}`),
  getAttendanceByStudent: (studentId) => request(`/attendance/student/${studentId}`),
  getMonthlyAttendance: (studentId, month, year) =>
    request(`/attendance/student/${studentId}/monthly?month=${month}&year=${year}`),
  saveAttendanceBulk: (list) => request('/attendance/bulk', { method: 'POST', body: JSON.stringify(list) }),

  // Exams
  getExams: () => request('/exams'),
  getExamsByClass: (classId) => request(`/exams/class/${classId}`),
  createExam: (data) => request('/exams', { method: 'POST', body: JSON.stringify(data) }),

  // Marks
  getMarksByExam: (examId) => request(`/marks/exam/${examId}`),
  getMarksByStudent: (studentId) => request(`/marks/student/${studentId}`),
  saveMarksBulk: (list) => request('/marks/bulk', { method: 'POST', body: JSON.stringify(list) }),

  // Fees
  getFeeStructure: () => request('/fees/structure'),
  getFeeStructureByClass: (classId) => request(`/fees/structure/class/${classId}`),
  createFeeStructure: (data) => request('/fees/structure', { method: 'POST', body: JSON.stringify(data) }),
  getFeePayments: () => request('/fees/payments'),
  getFeePaymentsByStudent: (studentId) => request(`/fees/payments/student/${studentId}`),
  createFeePayment: (data) => request('/fees/payments', { method: 'POST', body: JSON.stringify(data) }),

  // Salaries
  getSalaries: () => request('/salaries'),
  getPayroll: (month, year) => request(`/salaries/payroll?month=${month}&year=${year}`),
  getSalariesByTeacher: (teacherId) => request(`/salaries/teacher/${teacherId}`),
  createSalary: (data) => request('/salaries', { method: 'POST', body: JSON.stringify(data) }),
  updateSalary: (id, data) => request(`/salaries/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Holidays
  getHolidays: () => request('/holidays'),
  getHolidaysByYear: (year) => request(`/holidays/year/${year}`),
  getHolidaysByMonth: (month, year) => request(`/holidays/month?month=${month}&year=${year}`),
  createHoliday: (data) => request('/holidays', { method: 'POST', body: JSON.stringify(data) }),
  deleteHoliday: (id) => request(`/holidays/${id}`, { method: 'DELETE' }),
};

// ── Online Test APIs ────────────────────────────────────────────────────────

const TEST_ADMIN = '/spring/test-admin/api';
const TEST_STUDENT = '/spring/student/api';

async function testReq(base, path, options = {}) {
  const res = await fetch(base + path, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
    ...options,
  });
  if (res.redirected && res.url.includes('login')) { redirectToLogin(); return null; }
  if (res.status === 401 || res.status === 403) { redirectToLogin(); return null; }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('text/html')) { redirectToLogin(); return null; }
    const text = await res.text().catch(() => '');
    let msg = text;
    try { const j = JSON.parse(text); msg = j.error || j.message || text; } catch (_) {}
    throw new Error(msg || res.statusText);
  }
  if (res.status === 204) return null;
  const data = await res.json();
  if (data && data.error === 'Not authenticated') { redirectToLogin(); return null; }
  return data;
}

export const testAdminApi = {
  // subjects
  getSubjects: () => testReq(TEST_ADMIN, '/subjects'),
  saveSubject: (data) => testReq(TEST_ADMIN, '/subjects', { method: 'POST', body: JSON.stringify(data) }),
  updateSubject: (id, data) => testReq(TEST_ADMIN, `/subjects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSubject: (id) => testReq(TEST_ADMIN, `/subjects/${id}`, { method: 'DELETE' }),
  // questions
  getQuestions: (subjectId) => testReq(TEST_ADMIN, `/questions${subjectId ? '?subjectId=' + subjectId : ''}`),
  saveQuestion: (data) => testReq(TEST_ADMIN, '/questions', { method: 'POST', body: JSON.stringify(data) }),
  updateQuestion: (id, data) => testReq(TEST_ADMIN, `/questions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteQuestion: (id) => testReq(TEST_ADMIN, `/questions/${id}`, { method: 'DELETE' }),
  // tests
  getTests: () => testReq(TEST_ADMIN, '/tests'),
  getTest: (id) => testReq(TEST_ADMIN, `/tests/${id}`),
  saveTest: (data) => testReq(TEST_ADMIN, '/tests', { method: 'POST', body: JSON.stringify(data) }),
  updateTest: (id, data) => testReq(TEST_ADMIN, `/tests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTest: (id) => testReq(TEST_ADMIN, `/tests/${id}`, { method: 'DELETE' }),
  releaseTest: (id) => testReq(TEST_ADMIN, `/tests/${id}/release`, { method: 'POST' }),
  closeTest: (id) => testReq(TEST_ADMIN, `/tests/${id}/close`, { method: 'POST' }),
  getGrades: (testId) => testReq(TEST_ADMIN, `/tests/${testId}/grades`),
  saveGrades: (testId, grades) => testReq(TEST_ADMIN, `/tests/${testId}/grades`, { method: 'POST', body: JSON.stringify(grades) }),
  // results
  getResults: (testId, studentId) => {
    const p = new URLSearchParams();
    if (testId) p.append('testId', testId);
    if (studentId) p.append('studentId', studentId);
    return testReq(TEST_ADMIN, `/results?${p}`);
  },
  getResultDetail: (attemptId) => testReq(TEST_ADMIN, `/results/${attemptId}/detail`),
  // classes
  getClasses: () => testReq(TEST_ADMIN, '/classes'),
  saveClass: (data) => testReq(TEST_ADMIN, '/classes', { method: 'POST', body: JSON.stringify(data) }),
  updateClass: (id, data) => testReq(TEST_ADMIN, `/classes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteClass: (id) => testReq(TEST_ADMIN, `/classes/${id}`, { method: 'DELETE' }),
};

function schoolApiGuard(r) {
  if (r.status === 401 || r.status === 403) { redirectToLogin(); return null; }
  if (r.redirected && r.url.includes('login')) { redirectToLogin(); return null; }
  const ct = r.headers.get('content-type') || '';
  if (!r.ok && ct.includes('text/html')) { redirectToLogin(); return null; }
  return r.json().then(d => {
    if (d && d.error === 'Not authenticated') { redirectToLogin(); return null; }
    return d;
  });
}

export const schoolUserApi = {
  getUsers:       () => fetch('/spring/school-api/users', { credentials: 'include' }).then(schoolApiGuard),
  updateUser:     (userId, data) => fetch(`/spring/school-api/users/${encodeURIComponent(userId)}/update`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(schoolApiGuard),
  deleteUser:     (userId) => fetch(`/spring/school-api/users/${encodeURIComponent(userId)}`, {
    method: 'DELETE', credentials: 'include',
  }).then(schoolApiGuard),
  changePassword: (data) => fetch('/spring/school-api/users/change-password', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(schoolApiGuard),
  getStudentClasses: () => fetch('/spring/school-api/users/student-classes', { credentials: 'include' }).then(r => r.json()),
  assignClass: (userId, data) => fetch(`/spring/school-api/users/${encodeURIComponent(userId)}/assign-class`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(schoolApiGuard),
  removeClassAssignment: (userId) => fetch(`/spring/school-api/users/${encodeURIComponent(userId)}/assign-class`, {
    method: 'DELETE', credentials: 'include',
  }).then(schoolApiGuard),
};

// ── Builder Construction Management APIs ──────────────────────────────────────

const BUILDER = '/spring/builder/api';

async function builderReq(path, options = {}) {
  const res = await fetch(BUILDER + path, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
    ...options,
  });
  if (res.redirected && res.url.includes('login')) { redirectToLogin(); return null; }
  if (res.status === 401 || res.status === 403) { redirectToLogin(); return null; }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('text/html')) { redirectToLogin(); return null; }
    const text = await res.text().catch(() => '');
    let msg = text;
    try { const j = JSON.parse(text); msg = j.error || j.message || text; } catch (_) {}
    throw new Error(msg || res.statusText);
  }
  if (res.status === 204) return null;
  const data = await res.json();
  if (data && data.error === 'Not authenticated') { redirectToLogin(); return null; }
  return data;
}

export const builderApi = {
  // Projects
  getProjects:       ()         => builderReq('/projects'),
  getProjectSummary: (id)       => builderReq(`/projects/${id}/summary`),
  createProject:     (data)     => builderReq('/projects',       { method: 'POST',   body: JSON.stringify(data) }),
  updateProject:     (id, data) => builderReq(`/projects/${id}`, { method: 'PUT',    body: JSON.stringify(data) }),
  deleteProject:     (id)       => builderReq(`/projects/${id}`, { method: 'DELETE' }),
  // Materials
  getMaterials:   ()           => builderReq('/materials'),
  createMaterial: (data)       => builderReq('/materials',   { method: 'POST',   body: JSON.stringify(data) }),
  updateMaterial: (id, data)   => builderReq(`/materials/${id}`, { method: 'PATCH',  body: JSON.stringify(data) }),
  deleteMaterial: (id)         => builderReq(`/materials/${id}`, { method: 'DELETE' }),
  // Purchases
  getPurchases:      (pid)     => builderReq('/purchases' + (pid ? `?projectId=${pid}` : '')),
  getSupplierNames:  ()        => builderReq('/purchases/suppliers'),
  createPurchase:    (data)    => builderReq('/purchases',      { method: 'POST',   body: JSON.stringify(data) }),
  deletePurchase:    (id)      => builderReq(`/purchases/${id}`, { method: 'DELETE' }),
  // Inventory
  getInventory:           ()   => builderReq('/inventory'),
  getInventoryByProject:  ()   => builderReq('/inventory/project-breakdown'),
  // Issues
  getIssues:           (pid)   => builderReq('/issues' + (pid ? `?projectId=${pid}` : '')),
  getProjectMaterials: (pid)   => builderReq(`/issues/project-materials?projectId=${pid}`),
  createIssue:         (data)  => builderReq('/issues',      { method: 'POST',   body: JSON.stringify(data) }),
  deleteIssue:         (id)    => builderReq(`/issues/${id}`, { method: 'DELETE' }),
  // Work
  getWork:        (pid)        => builderReq('/work' + (pid ? `?projectId=${pid}` : '')),
  createWork:     (data)       => builderReq('/work',        { method: 'POST',   body: JSON.stringify(data) }),
  updateWork:     (id, data)   => builderReq(`/work/${id}`,  { method: 'PATCH',  body: JSON.stringify(data) }),
  deleteWork:     (id)         => builderReq(`/work/${id}`,  { method: 'DELETE' }),
  // Employees
  getEmployees:   ()           => builderReq('/employees'),
  createEmployee: (data)       => builderReq('/employees',   { method: 'POST',   body: JSON.stringify(data) }),
  deleteEmployee: (id)         => builderReq(`/employees/${id}`, { method: 'DELETE' }),
  // Payments
  getPayments:       (p)       => builderReq('/payments' + (p ? '?' + new URLSearchParams(p).toString() : '')),
  createPayment:     (data)    => builderReq('/payments',    { method: 'POST',   body: JSON.stringify(data) }),
  deletePayment:     (id)      => builderReq(`/payments/${id}`, { method: 'DELETE' }),
  getPaymentSummary: ()        => builderReq('/payments/summary'),
  // Supplier Payments
  getSupplierPayments:       (p)    => builderReq('/supplier-payments' + (p ? '?' + new URLSearchParams(p).toString() : '')),
  createSupplierPayment:     (data) => builderReq('/supplier-payments', { method: 'POST', body: JSON.stringify(data) }),
  deleteSupplierPayment:     (id)   => builderReq(`/supplier-payments/${id}`, { method: 'DELETE' }),
  getSupplierPaymentSummary: ()     => builderReq('/supplier-payments/summary'),
  getSupplierInvoices:       ()     => builderReq('/supplier-payments/invoices'),
  uploadInvoiceImage: (purchaseId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('purchaseId', purchaseId);
    return fetch(BUILDER + '/invoice-image', { method: 'POST', credentials: 'include', body: fd })
      .then(async r => { if (!r.ok) throw new Error(await r.text()); return r.json(); });
  },
  getInvoiceUrl: (key) => builderReq(`/invoice-url?key=${encodeURIComponent(key)}`),
  // Lookups (trades / roles)
  getLookups:    (cat)         => builderReq('/lookups' + (cat ? `?category=${cat}` : '')),
  createLookup:  (data)        => builderReq('/lookups',     { method: 'POST',   body: JSON.stringify(data) }),
  deleteLookup:  (id)          => builderReq(`/lookups/${id}`, { method: 'DELETE' }),
  // Documents
  listDocs: (folder) => builderReq('/docs' + (folder ? `?folder=${encodeURIComponent(folder)}` : '')),
  // Reports
  reportInventory:   ()           => builderReq('/reports/inventory'),
  reportProjectCost: ()           => builderReq('/reports/project-cost'),
  reportPurchases:   (from, to)   => builderReq(`/reports/purchases?${from?'from='+from:''}${to?'&to='+to:''}`),
};

export const testStudentApi = {
  getMe: () => testReq(TEST_STUDENT, '/me'),
  getAvailableTests: () => testReq(TEST_STUDENT, '/tests'),
  startTest: (testId) => testReq(TEST_STUDENT, `/test/${testId}/start`, { method: 'POST' }),
  getAttemptQuestions: (attemptId) => testReq(TEST_STUDENT, `/attempt/${attemptId}/questions`),
  submitTest: (attemptId, answers) => testReq(TEST_STUDENT, `/attempt/${attemptId}/submit`, { method: 'POST', body: JSON.stringify({ answers }) }),
  getMyResults: () => testReq(TEST_STUDENT, '/results'),
  getAttemptReview: (attemptId) => testReq(TEST_STUDENT, `/attempt/${attemptId}/review`),
};

// ── Document Manager APIs ────────────────────────────────────────────────────

const DOCS = '/spring/docs';

async function docsReq(path, options = {}) {
  const res = await fetch(DOCS + path, {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    credentials: 'include',
    ...options,
  });
  if (res.redirected && res.url.includes('login')) { redirectToLogin(); return null; }
  if (res.status === 401 || res.status === 403) { redirectToLogin(); return null; }
  if (!res.ok) {
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('text/html')) { redirectToLogin(); return null; }
    const t = await res.text().catch(() => '');
    throw new Error(t || res.statusText);
  }
  if (res.status === 204) return null;
  const data = await res.json();
  if (data && data.error === 'Not authenticated') { redirectToLogin(); return null; }
  return data;
}

export const docApi = {
  getFolders:   ()               => docsReq('/folders'),
  createFolder: (data)           => docsReq('/folders',       { method: 'POST',   body: JSON.stringify(data) }),
  renameFolder: (id, data)       => docsReq(`/folders/${id}`, { method: 'PUT',    body: JSON.stringify(data) }),
  deleteFolder: (id)             => docsReq(`/folders/${id}`, { method: 'DELETE' }),
  getFiles:     (folderId)       => docsReq('/files' + (folderId != null ? `?folderId=${folderId}` : '')),
  uploadFile:   (file, folderId) => {
    const fd = new FormData();
    fd.append('file', file);
    if (folderId != null) fd.append('folder_id', folderId);
    return docsReq('/upload', { method: 'POST', headers: {}, body: fd });
  },
  renameFile:   (id, data)       => docsReq(`/files/${id}`,   { method: 'PUT',    body: JSON.stringify(data) }),
  moveFile:     (id, data)       => docsReq(`/files/${id}`,   { method: 'PUT',    body: JSON.stringify(data) }),
  deleteFile:   (id)             => docsReq(`/files/${id}`,   { method: 'DELETE' }),
  presign:      (id)             => docsReq(`/presign/${id}`),
  search:       (q)              => docsReq(`/search?q=${encodeURIComponent(q)}`),
};
